import { Link } from 'react-router-dom';
import LearnPage from './LearnPage';

const FAQS = [
  {
    q: 'What is the difference between a REIT, BDC, and CEF?',
    a: 'REITs (Real Estate Investment Trusts) own real estate or mortgages. BDCs (Business Development Companies) lend money to private mid-sized businesses. CEFs (Closed-End Funds) are actively managed funds that trade on exchanges like stocks. All three are required by law to pay out most of their income as dividends, which is why they all yield much higher than the average S&P 500 stock.',
  },
  {
    q: 'Why do REITs, BDCs, and CEFs yield so much?',
    a: 'Tax law. REITs and BDCs are "pass-through" entities — they pay almost no corporate tax IF they distribute at least 90% (REITs) or roughly all (BDCs) of their taxable income to shareholders. CEFs distribute most of their realized gains and income to stay tax-efficient. The result: yields commonly run 5-12% vs ~1-2% for a typical S&P 500 stock.',
  },
  {
    q: 'Are these dividends qualified or ordinary?',
    a: 'Mostly ordinary, which means they are taxed at your regular income rate (10-37%) instead of the preferential qualified dividend rate (0/15/20%). The exception: some REITs distribute return-of-capital portions that defer tax to when you sell. Holding these in a Roth IRA or 401(k) is usually a much better tax outcome than holding them in a regular brokerage account.',
  },
  {
    q: 'Which is riskiest?',
    a: 'It depends on the cycle. BDCs are most sensitive to credit conditions — recessions and rising default rates hit them hardest. REITs are sensitive to interest rates — when rates rise, REIT prices fall because their dividends look less attractive vs bonds. CEFs depend on the underlying assets and the manager; leveraged CEFs add a layer of price volatility on top. None of these is "safe" the way a Treasury is safe; they trade growth for income.',
  },
  {
    q: 'Can I just buy an ETF that holds all three?',
    a: 'You can get close. Funds like VNQ hold REITs, BIZD holds BDCs, and various "income" ETFs blend the three. The trade-off: less concentration risk but lower yields than picking individual high-yielders. Most income investors hold a mix — an ETF or two for diversification plus a handful of high-conviction direct holdings.',
  },
];

export default function LearnReitsBdcsCefs() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  };

  return (
    <LearnPage
      title="REITs vs BDCs vs CEFs — High-Yield Investment Vehicles Compared (2026) | DiviDrip"
      description="The three high-yield vehicles that confuse new income investors, explained side by side. Structure, taxes, risk profile, and when each makes sense."
      canonical="https://twylightcrow.com/learn/reits-vs-bdcs-vs-cefs"
      guidePath="/learn/reits-vs-bdcs-vs-cefs"
      sectionLabel="Vehicles & Structures"
      sectionAnchor="vehicles"
      kicker="Learn · High-yield vehicles"
      h1="REITs vs BDCs vs CEFs — High-Yield Vehicles Compared"
      datePublished="2026-05-23"
      jsonLd={jsonLd}
      related={[
        { to: '/learn/clo-etfs-nesting-doll', label: 'CLO ETFs — the waterfall, the yield pick-up and the 0.00%-yield nesting-doll funds' },
        { to: '/learn/mlps-explained', label: 'MLPs explained — the other high-yield vehicle' },
        { to: '/learn/dividend-triangle', label: 'Reading the Dividend Triangle Score' },
        { to: '/learn/covered-call-etfs', label: 'Covered Call ETFs — JEPI, JEPQ, QYLD, XYLD' },
        { to: '/learn/qualified-vs-ordinary-dividends', label: 'Qualified vs ordinary dividends — tax guide' },
        { to: '/learn/dividend-traps', label: 'Dividend trap stocks — spotting yields that lie' },
        { to: '/learn/yield-on-cost', label: 'Yield on cost explained' },
        { to: '/glossary/cef-strategies', label: 'CEF strategy types — managed distribution, leverage, options' },
      ]}
    >
      <p>
        New dividend investors discover REITs first (everyone&apos;s heard of them), then
        stumble into BDCs and CEFs while chasing higher yields, and quickly hit a wall of
        acronyms. Here is the field manual.
      </p>

      <h2>The three at a glance</h2>
      <table className="learn-table">
        <thead>
          <tr>
            <th className="text-left font-semibold">Vehicle</th>
            <th className="text-left font-semibold">What it owns</th>
            <th className="text-left font-semibold">Typical yield</th>
            <th className="text-left font-semibold">Examples</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="font-semibold">REIT</td>
            <td>Real estate (or mortgages)</td>
            <td>3-12%</td>
            <td className="font-mono text-xs">O, STAG, AGNC, VNQ, PLD</td>
          </tr>
          <tr>
            <td className="font-semibold">BDC</td>
            <td>Loans to private companies</td>
            <td>8-13%</td>
            <td className="font-mono text-xs">MAIN, ARCC, OBDC, HTGC</td>
          </tr>
          <tr>
            <td className="font-semibold">CEF</td>
            <td>Anything (actively managed)</td>
            <td>6-15%</td>
            <td className="font-mono text-xs">PDI, UTG, NRGX, BST, RFI</td>
          </tr>
        </tbody>
      </table>

      <h2>REITs — the real-estate slice</h2>
      <p>
        REITs were created by Congress in 1960 to let ordinary investors own commercial
        real estate without buying buildings. Two flavours:
      </p>
      <ul>
        <li><strong>Equity REITs</strong> — own physical properties (Realty Income owns 15,000+ retail buildings; STAG owns industrial warehouses; AvalonBay owns apartments). Income comes from rent.</li>
        <li><strong>Mortgage REITs (mREITs)</strong> — own portfolios of mortgages or mortgage-backed securities (AGNC, NLY). They borrow short, lend long, and pocket the spread. High yields, high volatility, very sensitive to interest rates.</li>
      </ul>
      <p>
        REIT dividends are mostly ordinary (taxed at your income rate). Hold them in tax-
        advantaged accounts if you can.
      </p>

      <h2>BDCs — private-credit on the public market</h2>
      <p>
        Business Development Companies are essentially private-credit funds that you can
        buy on a stock exchange. They lend money to middle-market companies (typically
        $10M-$1B revenue) that are too big for a regional bank but too small for the
        public bond market. The interest payments flow through to shareholders as
        dividends.
      </p>
      <p>
        Why BDCs yield so much: they are required by law to distribute 90%+ of taxable
        income, AND most loans they make are floating-rate (the interest rate adjusts
        with the Fed funds rate), so they directly benefit when rates rise.
      </p>
      <p>
        The catch: BDC dividends are entirely ordinary income, AND the underlying loans
        are non-investment-grade by definition. When the credit cycle turns, BDC NAVs
        can drop fast.
      </p>

      <h2>CEFs — the actively managed wildcards</h2>
      <p>
        A Closed-End Fund issues a fixed number of shares at IPO and then trades on an
        exchange like a stock. Unlike an ETF, it doesn&apos;t create or redeem shares
        based on demand — so the share price can drift to a premium or discount vs the
        fund&apos;s underlying NAV. This creates the famous &ldquo;buying a dollar for
        90 cents&rdquo; opportunity dividend investors love.
      </p>
      <p>
        Many CEFs use leverage (borrowing to amplify returns), options-writing strategies,
        or specialised mandates (utilities, energy infrastructure, muni bonds, etc.) to
        push yields well above what the underlying assets would otherwise pay. The cost:
        active-management fees of 1-2%, and high sensitivity to whatever the manager is
        doing.
      </p>
      <p>
        See <Link to="/glossary/cef-strategies">CEF strategy types</Link> for a deeper
        dive into managed distributions, return of capital, and leverage math.
      </p>

      <h2>Tax treatment — the part everyone overlooks</h2>
      <ul>
        <li><strong>REITs</strong>: Ordinary income with a Section 199A deduction (20% off the federal portion in some accounts). Best in IRAs.</li>
        <li><strong>BDCs</strong>: Ordinary income, no special break. <em>Definitely</em> belongs in a tax-advantaged account.</li>
        <li><strong>CEFs</strong>: Mixed — depends on the fund. Many distribute return-of-capital, which defers tax to when you sell. Always check the 19a notices.</li>
      </ul>

      <h2>FAQ</h2>
      <dl className="learn-faq">
        {FAQS.map(({ q, a }) => (
          <div key={q} className="my-4">
            <dt className="font-semibold text-foreground">{q}</dt>
            <dd className="mt-1 text-foreground/85">{a}</dd>
          </div>
        ))}
      </dl>

      <h2>Try it</h2>
      <p>
        Open <Link to="/dashboard">DiviDrip</Link>, search for <strong>O</strong>{' '}
        (REIT), <strong>MAIN</strong> (BDC), and <strong>PDI</strong> (CEF). Compare the
        yield, payout frequency, and dividend history side by side. Then add one of each
        to your watchlist to see the dividend calendar fill in over time.
      </p>
    </LearnPage>
  );
}
